import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import { BoxShadowXl, ColorNeutralLightGrey, ColorNeutralWhite } from 'autogen/design-tokens/tokens';
import { Last, Next } from 'autogen/translation-keys/trans-website-paging';
import { ButtonWithIcon } from 'components/Button';
import type IButton from 'components/Button/shared/types/button';
import { useNavigation } from 'components/emblaCarousel';
import type { onCarouselScrollClick } from 'components/GalleryMediaPreviewer/shared/types';
import Icon from 'components/Icon';

export type ScrollType = 'next' | 'previous';
export type Direction = 'horizontal' | 'vertical';

/* #region  styles */
const ScrollButton = styled(ButtonWithIcon)`
    background-color: ${ColorNeutralWhite};
    box-shadow: ${BoxShadowXl};
    border: 1px solid ${ColorNeutralLightGrey};
    aspect-ratio: 1;
`;

const AnimatedScrollButton = animated<any>(ScrollButton);
/* #endregion */

interface ICarouselScrollButton extends IButton {
    scrollType: ScrollType;
    onClickCallBack?: onCarouselScrollClick;
    direction?: Direction;
}

export default function CarouselScrollButton({
    scrollType,
    onClickCallBack,
    size = 'lg',
    direction = 'horizontal',
    ...restProps
}: ICarouselScrollButton) {
    const { t } = useTranslation();
    const { prev, next, canScrollNext, canScrollPrev } = useNavigation();

    const iconType = {
        horizontal: { next: <Icon type="ChevronRight" size={16} />, previous: <Icon type="ChevronLeft" size={16} /> },
        vertical: { next: <Icon type="ChevronDown" size={16} />, previous: <Icon type="ChevronUp" size={16} /> }
    };

    const buttonType: any = {
        next: { icon: iconType[direction].next, canScroll: canScrollNext, onClick: next },
        previous: { icon: iconType[direction].previous, canScroll: canScrollPrev, onClick: prev }
    };

    const transitions = useTransition(buttonType[scrollType].canScroll, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        reverse: buttonType[scrollType].canScroll
    });

    return transitions(
        (styles, item) =>
            item && (
                <AnimatedScrollButton
                    {...restProps}
                    size={size}
                    style={styles}
                    icon={buttonType[scrollType].icon}
                    aria-label={scrollType === 'next' ? t(Next) : t(Last)}
                    onClick={() => {
                        if (onClickCallBack) {
                            onClickCallBack(scrollType);
                        }
                        buttonType[scrollType].onClick();
                    }}
                    iconProperties={{ size: '14px' }}
                    variant={null}
                />
            )
    );
}
